// Data store shared by the website and the admin panel.
// Content lives in the MySQL database and is read/written through the PHP API in
// public/api. A copy is kept in localStorage so pages show instantly on the next
// visit, then refresh from the server in the background.
import { useSyncExternalStore } from "react";
import seedPosts from "../data/seedPosts";
import { seedAuthors, seedTeam, seedSettings } from "../data/seedData";

export const KEYS = {
    posts: "posts",
    authors: "authors",
    team: "team",
    settings: "settings",
};

const SEEDS = {
    posts: seedPosts,
    authors: seedAuthors,
    team: seedTeam,
    settings: seedSettings,
};

const API = "/api/";
const CACHE_KEY = "mmat_cache_v1";
// Where the admin panel kept content before the database existed
const LEGACY_KEYS = { posts: "mmat_posts", authors: "mmat_authors", team: "mmat_team", settings: "mmat_settings" };

const listeners = new Set();
const notify = () => listeners.forEach((l) => l());

function readCache() {
    try {
        const c = JSON.parse(window.localStorage.getItem(CACHE_KEY) || "null");
        if (c && Array.isArray(c.posts) && Array.isArray(c.authors) && Array.isArray(c.team)) return c;
    } catch {}
    return null;
}

let state = { ...SEEDS, ...(typeof window !== "undefined" ? readCache() || {} : {}) };
state.settings = { ...seedSettings, ...state.settings };

function setState(patch) {
    state = { ...state, ...patch };
    if (patch.settings) state.settings = { ...seedSettings, ...patch.settings };
    try {
        window.localStorage.setItem(CACHE_KEY, JSON.stringify(state));
    } catch {}
    notify();
}

function subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function useStore(key) {
    return useSyncExternalStore(subscribe, () => state[key], () => SEEDS[key]);
}

// ---------- API ----------
export async function api(action, { method = "GET", body, query = "" } = {}) {
    let res;
    try {
        res = await fetch(`${API}?action=${action}${query}`, {
            method,
            credentials: "same-origin",
            headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
    } catch {
        throw new Error("Could not reach the server. Check your internet connection and try again.");
    }
    let data = null;
    try {
        data = await res.json();
    } catch {}
    if (!res.ok || !data) {
        if (res.status === 401) window.dispatchEvent(new Event("mmat:unauthorized"));
        throw new Error((data && data.error) || `Server error (${res.status}).`);
    }
    return data;
}

let installed = true;

// Load everything from the database. Until it has been set up (first admin
// sign-in), the site keeps showing the built-in default content.
export async function refresh() {
    const data = await api("data");
    installed = data.installed;
    if (installed) {
        setState({ posts: data.posts, authors: data.authors, team: data.team, settings: data.settings });
    }
    return data;
}

if (typeof window !== "undefined") {
    refresh().catch((e) => console.warn("[mmautotech] Showing saved content:", e.message));
}

// First admin sign-in on an empty database: copy in the content this browser
// already had (from the old browser-only admin panel), or the built-in defaults.
export async function installIfNeeded() {
    await refresh();
    if (installed) return false;
    const legacy = {};
    Object.entries(LEGACY_KEYS).forEach(([k, storageKey]) => {
        try {
            const raw = window.localStorage.getItem(storageKey);
            if (raw) legacy[k] = JSON.parse(raw);
        } catch {}
    });
    const settings = { ...seedSettings, ...(legacy.settings || {}) };
    // Contact details that have since been replaced on the website
    if (settings.email === "mmautotechmarketing@gmail.com") settings.email = seedSettings.email;
    if (settings.landline === "020 31 500845") settings.landline = "";
    await api("import", {
        method: "POST",
        body: {
            posts: Array.isArray(legacy.posts) ? legacy.posts : seedPosts,
            authors: Array.isArray(legacy.authors) ? legacy.authors : seedAuthors,
            team: Array.isArray(legacy.team) ? legacy.team : seedTeam,
            settings,
        },
    });
    Object.values(LEGACY_KEYS).forEach((k) => {
        try {
            window.localStorage.removeItem(k);
        } catch {}
    });
    await refresh();
    return true;
}

export const uid = (prefix) =>
    `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export const slugify = (text) =>
    String(text || "")
        .toLowerCase()
        .replace(/[’'"]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 80);

// ---------- Posts ----------
export const getPosts = () => state.posts;
export async function savePost(post) {
    const { post: saved } = await api("save-post", { method: "POST", body: { post } });
    const exists = state.posts.some((p) => p.id === saved.id);
    setState({ posts: exists ? state.posts.map((p) => (p.id === saved.id ? saved : p)) : [saved, ...state.posts] });
    return saved;
}
export async function deletePost(id) {
    await api("delete-post", { method: "DELETE", query: `&id=${encodeURIComponent(id)}` });
    setState({ posts: state.posts.filter((p) => p.id !== id) });
}
export const sortPosts = (posts) =>
    [...posts].sort((a, b) => String(b.date).localeCompare(String(a.date)));

// ---------- Authors ----------
export const getAuthors = () => state.authors;
export async function saveAuthor(author) {
    const { author: saved } = await api("save-author", { method: "POST", body: { author } });
    const exists = state.authors.some((a) => a.id === saved.id);
    setState({ authors: exists ? state.authors.map((a) => (a.id === saved.id ? saved : a)) : [...state.authors, saved] });
    return saved;
}
// The author's posts move to `reassignTo` (or are left without an author)
export async function deleteAuthor(id, reassignTo = "") {
    await api("delete-author", {
        method: "DELETE",
        query: `&id=${encodeURIComponent(id)}&reassignTo=${encodeURIComponent(reassignTo)}`,
    });
    setState({
        authors: state.authors.filter((a) => a.id !== id),
        posts: state.posts.map((p) => (p.authorId === id ? { ...p, authorId: reassignTo } : p)),
    });
}

// ---------- Team ----------
export async function saveTeam(list) {
    const { team } = await api("save-team", { method: "POST", body: { team: list } });
    setState({ team });
}

// ---------- Settings ----------
export async function saveSettings(settings) {
    const { settings: saved } = await api("save-settings", { method: "POST", body: { settings } });
    setState({ settings: saved });
}

// ---------- Backup ----------
export function exportAll() {
    return {
        exportedAt: new Date().toISOString(),
        posts: state.posts,
        authors: state.authors,
        team: state.team,
        settings: state.settings,
    };
}
export async function importAll(data) {
    if (!data || !Array.isArray(data.posts) || !Array.isArray(data.authors)) {
        throw new Error("This file is not a valid MM AutoTech backup.");
    }
    await api("import", {
        method: "POST",
        body: {
            posts: data.posts,
            authors: data.authors,
            team: Array.isArray(data.team) ? data.team : state.team,
            settings: { ...seedSettings, ...(data.settings || state.settings) },
        },
    });
    await refresh();
}
export function resetAll() {
    return importAll({ posts: seedPosts, authors: seedAuthors, team: seedTeam, settings: seedSettings });
}

// ---------- Helpers ----------
export const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso + (String(iso).length === 10 ? "T00:00:00" : ""));
    if (isNaN(d)) return iso;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

export const initials = (name) =>
    String(name || "?")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join("");

export const waLink = (number) => `https://wa.me/${String(number || "").replace(/[^0-9]/g, "")}`;
export const telLink = (number) => `tel:${String(number || "").replace(/[^0-9+]/g, "")}`;
export const prettyPhone = (number) => {
    const n = String(number || "").replace(/[^0-9+]/g, "");
    const m = n.match(/^\+44(\d{4})(\d{6})$/);
    return m ? `+44 ${m[1]} ${m[2]}` : number;
};
